'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { Link } from '@/src/i18n/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { Anchor, Button, Card, Divider, Paragraph } from '@zillow/constellation-10';
import { HpOtpSource } from '@zg-rentals/ts-hotpads-api-client';
import { css } from '@/styled-system/css';
import { Lock } from 'lucide-react';
import type { AuthMessageKey } from '@/src/lib/auth/status';
import { mapOAuthCallbackErrorToMessageKey } from '@/src/lib/auth/status';
import {
  localizePostLoginRedirect,
  resolvePostLoginRedirect,
  sanitizePostLoginRedirect,
} from '@/src/lib/auth/post-login-redirect';
import { getPrivacyPortalCompletionRedirect } from '@/src/lib/auth/privacy-portal';
import type { SupportedLocale } from '@/src/i18n/routing';
import { LEGAL_URLS } from '@/src/lib/legalUrls';
import { LucideIcon } from '@/src/components/LucideIcon';
import { LoginForm } from '@/src/components/features/auth/LoginForm';
import { AuthPage } from '../../_workflow/AuthPage';

const securityLine = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'tightest',
});

export function LoginPage() {
  const searchParams = useSearchParams();
  const t = useTranslations('auth');
  const locale = useLocale() as SupportedLocale;

  const requestedRedirect =
    getPrivacyPortalCompletionRedirect(searchParams.get('redirectUrl')) ??
    sanitizePostLoginRedirect(searchParams.get('redirect'));
  const postLoginRedirect = localizePostLoginRedirect(requestedRedirect ?? '/', locale);

  // Deferred from URL → local state so the Alert's aria-live region observes
  // a real post-mount mutation — banners present at first paint aren't
  // reliably announced by screen readers.
  const [callbackErrorKey, setCallbackErrorKey] = useState<AuthMessageKey | null>(null);
  useEffect(() => {
    setCallbackErrorKey(mapOAuthCallbackErrorToMessageKey(searchParams.get('error')));
  }, [searchParams]);

  const renderPrivacyDisclaimer = () => (
    <div className={css({ textAlign: 'center' })}>
      <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
        {t('privacyValue')}
      </Paragraph>
      <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
        {t.rich('privacyLinks', {
          privacy: (chunks) => (
            <Anchor href={LEGAL_URLS.privacyNotice} target="_blank" rel="noopener noreferrer">
              {chunks}
            </Anchor>
          ),
          terms: (chunks) => (
            <Anchor href={LEGAL_URLS.termsOfUse} target="_blank" rel="noopener noreferrer">
              {chunks}
            </Anchor>
          ),
        })}
      </Paragraph>
      <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
        <span className={securityLine}>
          <LucideIcon icon={Lock} size="sm" />
          {t('siteSecured')}
        </span>
      </Paragraph>
    </div>
  );

  const emailFooter = (
    <>
      <Divider css={{ marginBlock: 'loose' }} />
      {renderPrivacyDisclaimer()}
    </>
  );

  const renderPasswordFooter = (email: string) => (
    <>
      <Button asChild emphasis="bare" size="sm" tone="brand" fluid css={{ marginTop: 'default' }}>
        <Link href={`/forgot-password?email=${encodeURIComponent(email)}`} prefetch={false}>
          {t('forgotPassword')}
        </Link>
      </Button>

      <Divider css={{ marginBlock: 'loose' }} />

      {renderPrivacyDisclaimer()}
    </>
  );

  return (
    <AuthPage title={t('signInRegister')} description={t('introText')}>
      <Card>
        <LoginForm
          callbackErrorKey={callbackErrorKey}
          emailFooter={emailFooter}
          onAuthenticated={(response) => {
            const redirect = resolvePostLoginRedirect([requestedRedirect, response.forwardUrl]);
            window.location.replace(localizePostLoginRedirect(redirect, locale));
          }}
          onEmailError={(error) => console.error('[Auth] auth flow mutation failed', error)}
          onPasswordError={(error) => console.error('[Auth] email/password mutation failed', error)}
          passwordFooter={renderPasswordFooter}
          postLoginRedirect={postLoginRedirect}
          otpSource={HpOtpSource.LOGIN}
        />
      </Card>
    </AuthPage>
  );
}
